/**
 * Wellness-Scan frontend controller
 * 5-question quiz → spa-score (0-100) + product recommendations
 */
(() => {
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  const quiz = $('#wellness-quiz');
  const resultsMount = $('#wellness-results');
  const progressBar = $$('#wellness-progress span');
  const progressLabel = $('#wellness-progress-label');

  if (!quiz) return;

  let answers = [];
  const questions = $$('.wellness-question');
  let currentQuestion = 0;

  // Product mappings by score category
  const PRODUCT_TAGS = {
    essential: ['essentials', 'basics'],
    comfort: ['comfort', 'upgrades'],
    spa: ['spa', 'luxury', 'wellness'],
  };

  function setProgress(step) {
    progressBar.forEach((el, i) => {
      el.className = i < step ? 'flex-1 h-1 bg-ink rounded-full' : 'flex-1 h-1 bg-[color:var(--line)] rounded-full';
    });
    progressLabel.textContent = `Vraag ${step}/5`;
  }

  function showQuestion(n) {
    questions.forEach((q, i) => {
      q.style.display = i === n ? 'block' : 'none';
    });
    setProgress(n + 1);
  }

  function attachAnswerHandlers(questionNode) {
    const buttons = $$('.wellness-answer', questionNode);
    buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        answers.push(i);
        currentQuestion++;
        if (currentQuestion < questions.length) {
          showQuestion(currentQuestion);
        } else {
          showResults();
        }
      });
    });
  }

  questions.forEach(attachAnswerHandlers);

  function calcScore() {
    // Weighted scoring: each answer (0-2) contributes to spa-score
    // Q0: usage (0=quick, 2=spa) | Q1: inspiration (0=minimal, 2=spa)
    // Q2: color (0=cool, 2=warm/natural) | Q3: material (0=industrial, 2=luxury)
    // Q4: budget (0=low, 2=high)
    const weights = [0.2, 0.2, 0.15, 0.15, 0.3]; // Q4 budget matters most
    const score = answers.reduce((sum, ans, i) => sum + ans * weights[i] * 100, 0);
    return Math.round(Math.min(100, score));
  }

  function getCategoryAndProducts(score) {
    if (score >= 70) {
      return {
        category: 'Spa-ritueel pakket',
        tags: PRODUCT_TAGS.spa,
        description: 'Je hebt een voorkeur voor luxe en ontspanning. Deze set brengt je badkamer naar een spa-ervaring.',
      };
    } else if (score >= 40) {
      return {
        category: 'Comfort-upgrades',
        tags: PRODUCT_TAGS.comfort,
        description: 'Je zoekt balans tussen functionaliteit en stijl. Deze producten elevaten je dagelijkse routine.',
      };
    } else {
      return {
        category: 'Essentials',
        tags: PRODUCT_TAGS.essential,
        description: 'Je wilt betrouwbare basis-producten. Deze collectie is functioneel en betaalbaar.',
      };
    }
  }

  async function showResults() {
    const score = calcScore();
    const { category, description } = getCategoryAndProducts(score);

    // Fetch products from Shopify Storefront API via collection tags
    const products = await fetchProductsByTags(getCategoryAndProducts(score).tags);

    const html = resultsMarkup(score, category, description, products);
    resultsMount.innerHTML = html;
    resultsMount.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function fetchProductsByTags(tags) {
    // Fetch from Shopify Storefront API (public, no auth needed for product listing)
    // Fallback: return demo products if API unavailable
    try {
      const query = `
        query {
          productsByHandle(handles: [${tags.map(t => `"${t}"`).join(',')}]) {
            id
            title
            handle
            priceRange {
              minVariantPrice {
                amount
              }
            }
            featuredImage {
              url
            }
          }
        }
      `;
      const res = await fetch(`${window.location.origin}/api/graphql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      return (data.data?.productsByHandle || []).slice(0, 3);
    } catch (err) {
      console.warn('Could not fetch products:', err);
      // Return fallback product list (client-side hardcoded)
      return getDemoProducts();
    }
  }

  function getDemoProducts() {
    return [
      {
        id: '1',
        title: 'Wasstafel Japandi',
        handle: 'wasstafel-japandi',
        priceRange: { minVariantPrice: { amount: '899.00' } },
        featuredImage: { url: 'https://placehold.co/400x300?text=Wasstafel' },
      },
      {
        id: '2',
        title: 'Spiegel met LED',
        handle: 'spiegel-led',
        priceRange: { minVariantPrice: { amount: '599.00' } },
        featuredImage: { url: 'https://placehold.co/400x300?text=Spiegel' },
      },
      {
        id: '3',
        title: 'Kraan minimalistisch',
        handle: 'kraan-minimaal',
        priceRange: { minVariantPrice: { amount: '299.00' } },
        featuredImage: { url: 'https://placehold.co/400x300?text=Kraan' },
      },
    ];
  }

  function resultsMarkup(score, category, description, products) {
    const scoreColor = score >= 70 ? 'var(--moss)' : score >= 40 ? 'var(--paper)' : 'var(--line)';
    const productCards = (products || [])
      .map(
        (p) => `
      <a href="/products/${p.handle}" style="display: block; text-decoration: none; color: inherit;">
        <div style="position: relative; border-radius: 16px; overflow: hidden; background: var(--paper); aspect-ratio: 1/1;">
          <img src="${p.featuredImage?.url || 'https://placehold.co/300x300'}" style="width: 100%; height: 100%; object-fit: cover; display: block;" alt="${escapeHtml(p.title)}" loading="lazy" />
        </div>
        <p style="font-size: 14px; line-height: 1.4; color: var(--ink); margin: 12px 0 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${escapeHtml(p.title)}</p>
        <p style="font-size: 14px; font-weight: 500; color: var(--ink); margin: 0;">€${fmt(p.priceRange?.minVariantPrice?.amount || 0)}</p>
      </a>
    `
      )
      .join('');

    return `
      <section style="background: var(--bone); color: var(--ink); padding: 96px 0 112px;">
        <div style="max-width: 1280px; margin: 0 auto; padding: 0 24px;">
          <div style="text-align: center; margin-bottom: 64px;">
            <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 20px;">Je Spa-Score</p>
            <div style="font-family: 'Inter', ui-sans-serif, system-ui; font-weight: 700; font-size: 120px; line-height: 1; color: ${scoreColor}; margin: 0;">${score}</div>
            <p style="font-size: 20px; color: var(--ink); margin: 12px 0 24px;">${category}</p>
            <p style="font-size: 16px; color: var(--ink-soft); max-width: 680px; margin: 0 auto;">${description}</p>
          </div>

          <div style="margin-bottom: 64px;">
            <p style="font-size: 11px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); margin: 0 0 20px;">Aanbevolen producten</p>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 24px;">${productCards}</div>
          </div>

          <div style="text-align: center;">
            <a href="tel:+31641613498" style="display: inline-flex; align-items: center; gap: 10px; background: var(--ink); color: var(--bone); padding: 16px 28px; border-radius: 999px; font-size: 15px; text-decoration: none; transition: background 0.3s ease;">Vraag persoonlijk advies <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></a>
          </div>
        </div>
      </section>
    `;
  }

  function fmt(n) {
    return Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  showQuestion(0);
})();
